import React, { MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import CustomPopover from '@components/CustomPopover'
import Button from '@components/formControls/Button'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { ManageColumnsText, ManageColumnsWrapper } from '@modules/shortlist/index.styles'
import { ShortlistCategoriesPopoverProps } from '@modules/shortlist/types'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'

import {
  AddNewCustomColumnButton,
  ShortlistPopoverButtonsContainer,
  ShortlistPopoverWrapper,
  StyledColumnsConfigButton,
} from './index.styles'

const ShortlistCategoriesPopover = (props: ShortlistCategoriesPopoverProps) => {
  const {
    ndaFieldInitialValue,
    noteFieldInitialValue,
    tagIdFieldInitialValue,
    priorityInitialValue,
    dateAddedFieldInitialValue,
    companyFieldInitialValue,
    lastEventFieldInitialValue,
    futureEventsFieldInitialValue,
    contactStatusFieldInitialValue,
    updatedFieldInitialValue,
    phaseFieldInitialValue,
    activeShortlist,
    updateTableConfig,
    onUpsertCustomColumnClick,
    optionalColumnsConfig,
    anchorEl,
    setAnchorEl,
  } = props

  const { t } = useTranslation()
  const { user } = useAuthContext()
  const { canCreateCustomColumns } = useFeatureFlags()
  const { trackActivity } = useTrackUserActivity()
  const customFieldInitialValues = useMemo(() => {
    return (
      activeShortlist?.customFields?.reduce((acc, item) => {
        const { visible = true, width } = activeShortlist.columns?.[item.id as number] ?? {}
        return {
          ...acc,
          [item.id as number]: {
            value: visible,
            label: item.fieldName,
            width,
          },
        }
      }, {}) || {}
    )
  }, [activeShortlist?.customFields, activeShortlist?.columns])

  const [ndaFieldValue, setNdaFieldValue] = useState<boolean>(ndaFieldInitialValue?.visible)
  const [noteFieldValue, setNotesFieldValue] = useState<boolean>(noteFieldInitialValue?.visible)
  const [tagIdFieldValue, setTagFieldValue] = useState<boolean>(tagIdFieldInitialValue?.visible)
  const [priorityFieldValue, setPriorityFieldValue] = useState<boolean>(priorityInitialValue?.visible)
  const [dateAddedFieldValue, setDateAddedFieldValue] = useState<boolean>(dateAddedFieldInitialValue?.visible)
  const [companyFieldValue, setCompanyFieldValue] = useState<boolean>(companyFieldInitialValue?.visible)
  const [lastEventFieldValue, setLastEventFieldValue] = useState<boolean>(lastEventFieldInitialValue?.visible)
  const [futureEventsFieldValue, setFutureEventsFieldValue] = useState<boolean>(futureEventsFieldInitialValue?.visible)
  const [contactStatusFieldValue, setContactStatusFieldValue] = useState<boolean>(contactStatusFieldInitialValue?.visible)
  const [updatedFieldValue, setUpdatedFieldValue] = useState<boolean>(updatedFieldInitialValue?.visible)
  const [phaseFieldValue, setPhaseFieldValue] = useState<boolean>(phaseFieldInitialValue?.visible)
  const [customFieldValues, setCustomFieldValues] = useState<{
    [key: number]: { value: boolean; label: string; width?: number }
  }>({})
  const [confirmExitPopupCloseAction, setConfirmExitPopupCloseAction] = useState<Nullable<{ close: () => void }>>(null)

  useEffect(() => {
    setCustomFieldValues(customFieldInitialValues || {})
  }, [customFieldInitialValues])

  useEffect(() => {
    setNdaFieldValue(ndaFieldInitialValue?.visible)
    setNotesFieldValue(noteFieldInitialValue?.visible)
    setTagFieldValue(tagIdFieldInitialValue?.visible)
    setPriorityFieldValue(priorityInitialValue?.visible)
    setCompanyFieldValue(companyFieldInitialValue?.visible)
    setLastEventFieldValue(lastEventFieldInitialValue?.visible)
    setFutureEventsFieldValue(futureEventsFieldInitialValue?.visible)
    setContactStatusFieldValue(contactStatusFieldInitialValue?.visible)
    setUpdatedFieldValue(updatedFieldInitialValue?.visible)
    setPhaseFieldValue(phaseFieldInitialValue?.visible)
    setDateAddedFieldValue(dateAddedFieldInitialValue?.visible)
  }, [
    ndaFieldInitialValue,
    noteFieldInitialValue,
    tagIdFieldInitialValue,
    priorityInitialValue,
    companyFieldInitialValue,
    lastEventFieldInitialValue,
    futureEventsFieldInitialValue,
    contactStatusFieldInitialValue,
    updatedFieldInitialValue,
    phaseFieldInitialValue,
    dateAddedFieldInitialValue,
  ])

  const updateConfigurationButtonIcon = useMemo(
    () => (
      <>
        <AddIcon fontSize="small" />
        <RemoveIcon fontSize="small" />
      </>
    ),
    [],
  )

  const customFieldsEdited = useMemo(() => {
    const edited = Object.keys(customFieldInitialValues || {}).some((key: string) => {
      // @ts-expect-error fix-typing
      return customFieldInitialValues?.[Number(key)]?.value !== customFieldValues[Number(key)]?.value
    })
    return edited
  }, [customFieldInitialValues, customFieldValues])

  const hasEdited = useMemo(
    () =>
      ndaFieldInitialValue?.visible !== ndaFieldValue ||
      noteFieldInitialValue?.visible !== noteFieldValue ||
      tagIdFieldInitialValue?.visible !== tagIdFieldValue ||
      priorityFieldValue !== priorityInitialValue?.visible ||
      companyFieldValue !== companyFieldInitialValue?.visible ||
      lastEventFieldValue !== lastEventFieldInitialValue?.visible ||
      futureEventsFieldValue !== futureEventsFieldInitialValue?.visible ||
      contactStatusFieldValue !== contactStatusFieldInitialValue?.visible ||
      updatedFieldValue !== updatedFieldInitialValue?.visible ||
      phaseFieldValue !== phaseFieldInitialValue?.visible ||
      dateAddedFieldValue !== dateAddedFieldInitialValue?.visible ||
      customFieldsEdited,
    [
      ndaFieldInitialValue?.visible,
      ndaFieldValue,
      noteFieldInitialValue?.visible,
      noteFieldValue,
      tagIdFieldInitialValue?.visible,
      tagIdFieldValue,
      priorityFieldValue,
      priorityInitialValue?.visible,
      companyFieldValue,
      companyFieldInitialValue?.visible,
      lastEventFieldValue,
      lastEventFieldInitialValue?.visible,
      futureEventsFieldValue,
      futureEventsFieldInitialValue?.visible,
      contactStatusFieldValue,
      contactStatusFieldInitialValue?.visible,
      updatedFieldValue,
      updatedFieldInitialValue?.visible,
      phaseFieldValue,
      phaseFieldInitialValue?.visible,
      dateAddedFieldValue,
      dateAddedFieldInitialValue?.visible,
      customFieldsEdited,
    ],
  )

  const handlePopoverClose = useCallback(() => {
    if (hasEdited) {
      setConfirmExitPopupCloseAction({ close: () => setAnchorEl(null) })
    } else {
      setAnchorEl(null)
    }
  }, [hasEdited, setAnchorEl])

  const handleCategoriesClick = useCallback(
    (event: Nullable<MouseEvent<HTMLElement>>) => {
      if (event?.currentTarget) {
        setAnchorEl(event.currentTarget)
      } else {
        handlePopoverClose()
      }
    },
    [setAnchorEl, handlePopoverClose],
  )

  const handleSubmit = () => {
    const customFieldsConfig = Object.entries(customFieldValues).reduce(
      (acc, [id, item]) => ({
        ...acc,
        [id]: { visible: item.value, width: item.width ?? 150 },
      }),
      {},
    )

    updateTableConfig({
      ...optionalColumnsConfig,
      nda: { ...ndaFieldInitialValue, visible: ndaFieldValue },
      note: { ...noteFieldInitialValue, visible: noteFieldValue },
      tagId: { ...tagIdFieldInitialValue, visible: tagIdFieldValue },
      priority: { ...priorityInitialValue, visible: priorityFieldValue },
      createdAt: { ...dateAddedFieldInitialValue, visible: dateAddedFieldValue },
      company: { ...companyFieldInitialValue, visible: companyFieldValue },
      lastEvent: { ...lastEventFieldInitialValue, visible: lastEventFieldValue },
      futureEvents: { ...futureEventsFieldInitialValue, visible: futureEventsFieldValue },
      contactStatus: { ...contactStatusFieldInitialValue, visible: contactStatusFieldValue },
      updatedAt: { ...updatedFieldInitialValue, visible: updatedFieldValue },
      phase: { ...phaseFieldInitialValue, visible: phaseFieldValue },
      ...customFieldsConfig,
    })

    setAnchorEl(null)
  }
  const handleCancelSetCategories = () => {
    handlePopoverClose()
  }

  const trackToggleColumnClick = useCallback(
    ({ column, value }: { column: string; value: boolean }) => {
      trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.COLUMNS_VISIBILITY_MODAL.COLUMN_TOGGLE.CLICK, {
        column,
        status: value ? 'displayed' : 'hidden',
      })
    },
    [trackActivity],
  )

  const handleSetNdaField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'nda', value })
      setNdaFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetNotesField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'notes', value })
      setNotesFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetTagIdField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'status', value })
      setTagFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetDateAddedField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'date_added', value })
      setDateAddedFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetPriorityField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'priority', value })
      setPriorityFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetCustomField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
      setCustomFieldValues({
        ...customFieldValues,
        [id]: {
          ...customFieldValues[id],
          value: event.target.checked,
        },
      })
    },
    [customFieldValues],
  )

  const handleSetCompanyField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'company', value })
      setCompanyFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetLastEventField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'last_event', value })
      setLastEventFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetFutureEventsField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'future_event', value })
      setFutureEventsFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetContactStatusField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'contact_status', value })
      setContactStatusFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetUpdatedField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'updated_at', value })
      setUpdatedFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleSetPhaseField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      trackToggleColumnClick({ column: 'phase', value })
      setPhaseFieldValue(value)
    },
    [trackToggleColumnClick],
  )

  const handleCancelEdit = useCallback(() => {
    confirmExitPopupCloseAction?.close?.()
    setConfirmExitPopupCloseAction(null)
    setNdaFieldValue(ndaFieldInitialValue?.visible)
    setNotesFieldValue(noteFieldInitialValue?.visible)
    setTagFieldValue(tagIdFieldInitialValue?.visible)
    setPriorityFieldValue(priorityInitialValue?.visible)
    setDateAddedFieldValue(dateAddedFieldInitialValue?.visible)
    setCompanyFieldValue(companyFieldInitialValue?.visible)
    setLastEventFieldValue(lastEventFieldInitialValue?.visible)
    setFutureEventsFieldValue(futureEventsFieldInitialValue?.visible)
    setContactStatusFieldValue(contactStatusFieldInitialValue?.visible)
    setUpdatedFieldValue(updatedFieldInitialValue?.visible)
    setPhaseFieldValue(phaseFieldInitialValue?.visible)
    setCustomFieldValues(customFieldInitialValues || {})
  }, [
    confirmExitPopupCloseAction,
    ndaFieldInitialValue?.visible,
    noteFieldInitialValue?.visible,
    tagIdFieldInitialValue?.visible,
    priorityInitialValue?.visible,
    dateAddedFieldInitialValue?.visible,
    companyFieldInitialValue?.visible,
    lastEventFieldInitialValue?.visible,
    futureEventsFieldInitialValue?.visible,
    contactStatusFieldInitialValue?.visible,
    updatedFieldInitialValue?.visible,
    phaseFieldInitialValue?.visible,
    customFieldInitialValues,
  ])

  if (!user || !user.p1PlusAccessConfig?.optionalShortlistColumns) return null

  return (
    <>
      <ManageColumnsWrapper $width="186.51px" onClick={handleCategoriesClick} id="shortlist-manage_columns">
        <ManageColumnsText>{t(translationKeys.MANAGE_COLUMNS)}</ManageColumnsText>
        <StyledColumnsConfigButton data-testid="shortlist-config-button">
          {updateConfigurationButtonIcon}
        </StyledColumnsConfigButton>
      </ManageColumnsWrapper>
      <CustomPopover id="shortlist-categories-popoverId" anchor={anchorEl} setAnchor={handlePopoverClose}>
        <ShortlistPopoverWrapper data-testid="shortlist-categories-popoverId-testid">
          <Typography variant="h2" sx={{ marginBottom: '2px' }}>
            {t(translationKeys.MANAGE_COLUMNS)}
          </Typography>
          <Typography variant="body1">{t(translationKeys.SHOW_AND_HIDE_COLUMNS)}</Typography>
          <FormControlLabel
            value="company"
            control={<Switch color="primary" onChange={handleSetCompanyField} checked={companyFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_COMPANY_NAME)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="lastEvent"
            control={<Switch color="primary" onChange={handleSetLastEventField} checked={lastEventFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_LAST_EVENT)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="futureEvents"
            control={<Switch color="primary" onChange={handleSetFutureEventsField} checked={futureEventsFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_FUTURE_EVENTS)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="statusType"
            control={<Switch color="primary" onChange={handleSetContactStatusField} checked={contactStatusFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_CONTACT_STATUS)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="updatedAt"
            control={<Switch color="primary" onChange={handleSetUpdatedField} checked={updatedFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_UPDATED)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="createdAt"
            control={<Switch color="primary" onChange={handleSetDateAddedField} checked={dateAddedFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_DATE_ADDED)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="nda"
            control={<Switch color="primary" onChange={handleSetNdaField} checked={ndaFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_NDA)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="priority"
            control={<Switch color="primary" onChange={handleSetPriorityField} checked={priorityFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_PRIORITY)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="tagId"
            control={<Switch color="primary" onChange={handleSetTagIdField} checked={tagIdFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_CUSTOM_TAG)}
            labelPlacement="end"
          />
          {Object.keys(customFieldValues).map(key => (
            <FormControlLabel
              key={key}
              value={customFieldValues[Number(key)]?.value}
              control={
                <Switch
                  color="primary"
                  onChange={e => handleSetCustomField(e, Number(key))}
                  checked={!!customFieldValues[Number(key)]?.value}
                />
              }
              label={customFieldValues[Number(key)]?.label}
              labelPlacement="end"
            />
          ))}
          <FormControlLabel
            value="note"
            control={<Switch color="primary" onChange={handleSetNotesField} checked={noteFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_NOTES)}
            labelPlacement="end"
          />
          <FormControlLabel
            value="phase"
            control={<Switch color="primary" onChange={handleSetPhaseField} checked={phaseFieldValue} />}
            label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_PHASE)}
            labelPlacement="end"
          />

          {canCreateCustomColumns && (
            <AddNewCustomColumnButton startIcon={<AddIcon color="inherit" />} onClick={onUpsertCustomColumnClick}>
              {t(translationKeys.ADD_CUSTOM_COLUMN)}
            </AddNewCustomColumnButton>
          )}
          <ShortlistPopoverButtonsContainer>
            <Button variant="outlined" onClick={handleCancelSetCategories}>
              {t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!hasEdited}>
              {t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
            </Button>
          </ShortlistPopoverButtonsContainer>
        </ShortlistPopoverWrapper>
      </CustomPopover>
      {confirmExitPopupCloseAction && (
        <ConfirmPopup
          id="shortlist-table-confirm-exit-popup"
          width={294}
          contentWidth={180}
          isOpen
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setConfirmExitPopupCloseAction(null)}
          onConfirm={handleCancelEdit}
        />
      )}
    </>
  )
}

export default memo(ShortlistCategoriesPopover)
